import { useContext, useState } from "react"
import { Button, Table } from "reactstrap"
import { toast } from "react-toastify"

import AppContext from "AppContext"
import ordemServicoService, { StatusOrdemServicoEnum, StatusDivergenciaEnum, StatusCorrecaoEnum } from "services/OrdemServicoService"
import arquivoService from "services/ArquivoService"
import { confirm as conf } from "components/Confirmation"
import { showLoading, hideLoading } from "components/Loading"
import { erroInesperado } from "services/Utils"
import ModalLancarDivergencia from "./ModalLancarDivergencia"
import ModalConferirCorrecao from "./ModalConferirCorrecao"

const confirm = (text, onConfirm) => {
  conf({
    header: 'Fiscalização',
    text,
    icon: 'bi-exclamation-triangle',
    onConfirm
  })
}

export default function Fiscalizacao({ os, idOrdemServico, itens, onChange }) {
  const { state } = useContext(AppContext)
  const [configModalLancarDivergencia, setConfigModalLancarDivergencia] = useState()
  const [configModalConferirCorrecao, setConfigModalConferirCorrecao] = useState()

  const acoesUsuario = state?.user?.acoes || []
  const souResponsavelFiscalizacao = acoesUsuario.includes('os.iniciar_fiscalizacao') &&
    ((os.contrato_lote_idsFiscais || []).some(idFiscal => idFiscal == state.user.id) || os.contrato_idGestor == state.user.id)

  const handleIniciar = () => {
    confirm('Tem certeza que gostaria de iniciar a fiscalização desta Ordem de Serviço?', async () => {
      try {
        showLoading()
        await ordemServicoService.iniciarFiscalizacao(idOrdemServico)
        hideLoading()
        toast('Fiscalização iniciada')
        onChange && onChange()
      } catch (ex) {
        hideLoading()
        erroInesperado()
      }
    })
  }

  const handlePausar = () => {
    confirm('Tem certeza que gostaria de pausar a fiscalização? A Ordem de Serviço ficará disponível para reinício.', async () => {
      try {
        showLoading()
        await ordemServicoService.pausarFiscalizacao(idOrdemServico)
        hideLoading()
        toast('Fiscalização pausada')
        onChange && onChange()
      } catch (ex) {
        hideLoading()
        erroInesperado()
      }
    })
  }

  const handleFinalizar = () => {
    confirm('Tem certeza que gostaria de finalizar a fiscalização desta Ordem de Serviço?', async () => {
      try {
        showLoading()
        await ordemServicoService.finalizarFiscalizacao(idOrdemServico)
        hideLoading()
        toast('Fiscalização finalizada')
        onChange && onChange()
      } catch (ex) {
        hideLoading()
        erroInesperado()
      }
    })
  }

  const handleLancarDivergencia = (item) => {
    setConfigModalLancarDivergencia({
      item,
      onSubmit: async (observacao, fotos) => {
        try {
          showLoading()
          const tokens = []
          for (const foto of fotos) {
            const res = await arquivoService.upload(foto)
            if (!res.success) {
              hideLoading()
              toast('Erro ao enviar arquivo')
              return
            }
            tokens.push(res.data)
          }

          await ordemServicoService.lancarDivergencia(idOrdemServico, item.id, observacao, tokens)
          hideLoading()
          toast('Divergência lançada')
          onChange && onChange()
        } catch (ex) {
          hideLoading()
          erroInesperado()
        }
      }
    })
  }

  const handleConferirCorrecao = async (item) => {
    try {
      showLoading()
      const res = await ordemServicoService.getItemById(idOrdemServico, item.id)
      hideLoading()

      if (!res.success) {
        erroInesperado()
        return
      }

      const pendentes = []
      for (const divergencia of res.data.divergencias) {
        if (divergencia.status !== StatusDivergenciaEnum.AGUARDANDO_CONFERENCIA)
          continue

        const correcao = divergencia.correcoes.find(c => c.status === StatusCorrecaoEnum.PENDENTE)
        if (correcao)
          pendentes.push({ divergencia, correcao })
      }

      if (!pendentes.length) {
        toast('Não há correções pendentes de conferência para este item')
        return
      }

      setConfigModalConferirCorrecao({ idOrdemServico, item, pendentes })
    } catch (ex) {
      hideLoading()
      erroInesperado()
    }
  }

  if (!souResponsavelFiscalizacao) {
    return (
      <div className="text-dark">Você não tem permissão para operar a fiscalização desta Ordem de Serviço.</div>
    )
  }

  const aguardandoFiscalizacao = os.idStatus == StatusOrdemServicoEnum.AGUARDANDO_FISCALIZACAO
  const emFiscalizacao = os.idStatus == StatusOrdemServicoEnum.EM_FISCALIZACAO

  return (
    <div>
      {(aguardandoFiscalizacao || emFiscalizacao) && (
        <>
          <div className="d-flex flex-row justify-content-end mb-3" style={{ gap: 8 }}>
            {aguardandoFiscalizacao && (
              <Button color="primary" onClick={handleIniciar}>Iniciar Fiscalização</Button>
            )}
            {emFiscalizacao && (
              <>
                <Button color="primary" outline onClick={handlePausar}>Pausar Fiscalização</Button>
                <Button color="primary" onClick={handleFinalizar}>Finalizar Fiscalização</Button>
              </>
            )}
          </div>
          <Table>
            <thead>
              <tr>
                <th>Código</th>
                <th>Descrição</th>
                <th>Divergências</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {itens && itens.map(item => (
                <tr key={item.id}>
                  <td>{item.codigo}</td>
                  <td style={{ maxWidth: 400, whiteSpace: 'normal', wordBreak: 'break-word' }}>{item.descricao}</td>
                  <td>{item.divergencias}</td>
                  <td className="text-right">
                    <div className="d-flex flex-row justify-content-end" style={{ gap: 8 }}>
                      {item.divergenciasAguardandoConferencia > 0 && (
                        <Button color="warning" size="sm" disabled={!emFiscalizacao} onClick={() => handleConferirCorrecao(item)}>Conferir Correção</Button>
                      )}
                      <Button color="primary" size="sm" disabled={!emFiscalizacao} onClick={() => handleLancarDivergencia(item)}>Lançar Divergência</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      )}

      {(!aguardandoFiscalizacao && !emFiscalizacao) && (
        <div className="text-dark">Esta Ordem de Serviço não está em fase de fiscalização.</div>
      )}

      <ModalLancarDivergencia config={configModalLancarDivergencia} onClosed={() => setConfigModalLancarDivergencia()} />
      <ModalConferirCorrecao config={configModalConferirCorrecao} onClosed={() => setConfigModalConferirCorrecao()} onChange={onChange} />
    </div>
  )
}
